import { EventTimeline, MatrixClient, MatrixEvent } from 'matrix-js-sdk/src/matrix';
import { MatrixRTCSession } from 'matrix-js-sdk/src/matrixrtc/MatrixRTCSession';

import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';

export const HANGUP_MEMBERSHIP_STABILITY_ATTEMPTS = 3;
export const HANGUP_MEMBERSHIP_STABILITY_INTERVAL_MS = 150;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const isJoinEventType = (type: string) =>
  type === CustomMeedioMatrixEvents.USER_JOINED_MEETING || type === CustomMeedioMatrixEvents.USERS_JOINED_MEETING;

const isLeftEventType = (type: string) => type === CustomMeedioMatrixEvents.USER_LEFT_MEETING;

/** Host or last-participant hangup — not homeserver delayed cleanup. */
export const isExplicitMeetingEnd = (content: Record<string, unknown>): boolean => {
  const endedBy = content.ended_by as string | undefined;
  const source = content.source as string | undefined;
  return !!endedBy && source !== 'delayed_event';
};

/**
 * Whether a single MEETING_ENDED event should mark the session as ended.
 * Explicit ends always count; otherwise use per-user join/left before end ts.
 */
export const isEndEventTerminalForSession = (
  endEvent: MatrixEvent,
  timelineEvents: MatrixEvent[],
  meetingRoomId: string
): boolean => {
  const endContent = endEvent.getContent() ?? {};
  if (isExplicitMeetingEnd(endContent)) return true;

  const endTs = endEvent.getTs();
  const lastJoinTs = new Map<string, number>();
  const lastLeftTs = new Map<string, number>();

  for (const e of timelineEvents) {
    const type = e.getType();
    if (!isJoinEventType(type) && !isLeftEventType(type)) continue;
    if ((e.getContent()?.meeting_room_id as string | undefined) !== meetingRoomId) continue;
    const ts = e.getTs();
    if (ts >= endTs) continue;
    const sender = e.getSender();
    if (!sender) continue;
    if (isJoinEventType(type)) {
      if (ts > (lastJoinTs.get(sender) ?? -1)) lastJoinTs.set(sender, ts);
    } else {
      if (ts > (lastLeftTs.get(sender) ?? -1)) lastLeftTs.set(sender, ts);
    }
  }

  const hasActiveParticipant = Array.from(lastJoinTs.entries()).some(
    ([sender, joinTs]) => joinTs > (lastLeftTs.get(sender) ?? -1)
  );

  return !hasActiveParticipant;
};

export type IsMessengerMeetingSessionEndedParams = {
  matrixClient: MatrixClient;
  messengerRoomId: string;
  meetingRoomId: string;
  sessionStartedAtMs: number;
};

const collectJoinLeftStateAfterSession = (
  timelineEvents: MatrixEvent[],
  meetingRoomId: string,
  sessionStartedAtMs: number
) => {
  const lastJoinTs = new Map<string, number>();
  const lastLeftTs = new Map<string, number>();
  let hasJoinOrLeftAfterSession = false;

  for (const e of timelineEvents) {
    const type = e.getType();
    if (!isJoinEventType(type) && !isLeftEventType(type)) continue;
    if ((e.getContent()?.meeting_room_id as string | undefined) !== meetingRoomId) continue;
    const ts = e.getTs();
    if (ts <= sessionStartedAtMs) continue;
    hasJoinOrLeftAfterSession = true;
    const sender = e.getSender();
    if (!sender) continue;
    if (isJoinEventType(type)) {
      if (ts > (lastJoinTs.get(sender) ?? -1)) lastJoinTs.set(sender, ts);
    } else {
      if (ts > (lastLeftTs.get(sender) ?? -1)) lastLeftTs.set(sender, ts);
    }
  }

  const hasActiveParticipant = Array.from(lastJoinTs.entries()).some(
    ([sender, joinTs]) => joinTs > (lastLeftTs.get(sender) ?? -1)
  );

  return { hasJoinOrLeftAfterSession, hasActiveParticipant };
};

/** True when any join/left lifecycle row exists for this session after sessionStartedAtMs. */
export const hasMeetingJoinOrLeftActivityForSession = ({
  matrixClient,
  messengerRoomId,
  meetingRoomId,
  sessionStartedAtMs,
}: IsMessengerMeetingSessionEndedParams): boolean => {
  const room = matrixClient.getRoom(messengerRoomId);
  if (!room) return false;
  return collectJoinLeftStateAfterSession(room.getLiveTimeline().getEvents(), meetingRoomId, sessionStartedAtMs)
    .hasJoinOrLeftAfterSession;
};

/**
 * True when at least one participant is still "in" the meeting per timeline join/left rows.
 * Used when the local client has no RTC memberships (e.g. observer never joined the call space).
 */
export const hasActiveParticipantForMeetingSession = ({
  matrixClient,
  messengerRoomId,
  meetingRoomId,
  sessionStartedAtMs,
}: IsMessengerMeetingSessionEndedParams): boolean => {
  const room = matrixClient.getRoom(messengerRoomId);
  if (!room) return false;
  return collectJoinLeftStateAfterSession(room.getLiveTimeline().getEvents(), meetingRoomId, sessionStartedAtMs)
    .hasActiveParticipant;
};

const getMessengerCallState = (messengerRoom: NonNullable<ReturnType<MatrixClient['getRoom']>>) => {
  const roomState = messengerRoom.getLiveTimeline().getState(EventTimeline.FORWARDS);
  return roomState?.getStateEvents(CustomMeedioMatrixEvents.MESSENGER_CALL, '');
};

const hasNoRtcMembershipsInMeetingRoom = (matrixClient: MatrixClient, meetingRoomId: string): boolean => {
  const callRoom = matrixClient.getRoom(meetingRoomId);
  if (!callRoom) return true;
  const rtcSession = matrixClient.matrixRTC.getActiveRoomSession(callRoom);
  return !rtcSession || rtcSession.memberships.length === 0;
};

/**
 * True when this MEETING_STARTED session is over for observers and timeline rows.
 * Uses MEETING_ENDED when present; otherwise infers from MESSENGER_CALL supersession,
 * cleared call state, and join/left rows when the call space has no RTC members.
 */
export const isMessengerMeetingSessionEnded = ({
  matrixClient,
  messengerRoomId,
  meetingRoomId,
  sessionStartedAtMs,
}: IsMessengerMeetingSessionEndedParams): boolean => {
  const room = matrixClient.getRoom(messengerRoomId);
  if (!room) return false;

  const timelineEvents = room.getLiveTimeline().getEvents();

  const hasTerminalMeetingEnded = timelineEvents.some((event) => {
    if (event.getType() !== CustomMeedioMatrixEvents.MEETING_ENDED) return false;
    if ((event.getContent()?.meeting_room_id as string | undefined) !== meetingRoomId) return false;
    if (event.getTs() <= sessionStartedAtMs) return false;
    return isEndEventTerminalForSession(event, timelineEvents, meetingRoomId);
  });
  if (hasTerminalMeetingEnded) return true;

  const callState = getMessengerCallState(room);
  const liveCallRoomId = callState?.getContent()?.call_room_id as string | undefined;

  if (liveCallRoomId && liveCallRoomId !== meetingRoomId) return true;

  // Host hangup clears MESSENGER_CALL to `{}` while timeline rows may still show USER_JOINED only.
  if (callState && !liveCallRoomId) return true;

  const { hasJoinOrLeftAfterSession, hasActiveParticipant } = collectJoinLeftStateAfterSession(
    timelineEvents,
    meetingRoomId,
    sessionStartedAtMs
  );

  const everyoneLeftPerTimeline = hasJoinOrLeftAfterSession && !hasActiveParticipant;
  if (!everyoneLeftPerTimeline) return false;

  return hasNoRtcMembershipsInMeetingRoom(matrixClient, meetingRoomId);
};

export const countOtherHumanParticipants = (
  mxRtcSession: MatrixRTCSession,
  myUserId: string | null | undefined
): number => {
  if (!myUserId) return mxRtcSession.memberships.length;
  return mxRtcSession.memberships.filter((m) => m.sender !== myUserId).length;
};

/**
 * Returns true only when no other human RTC members remain after stability polling.
 * Re-reads memberships between attempts to avoid false "last participant" on disconnect races.
 */
export const confirmNoOtherHumanParticipants = async (
  mxRtcSession: MatrixRTCSession,
  myUserId: string | null | undefined
): Promise<boolean> => {
  for (let attempt = 0; attempt < HANGUP_MEMBERSHIP_STABILITY_ATTEMPTS; attempt++) {
    if (countOtherHumanParticipants(mxRtcSession, myUserId) > 0) return false;
    if (attempt < HANGUP_MEMBERSHIP_STABILITY_ATTEMPTS - 1) {
      await sleep(HANGUP_MEMBERSHIP_STABILITY_INTERVAL_MS);
    }
  }
  return true;
};
